import React, { useEffect, useMemo, useState } from "react";
import SectionCard from "../ui/SectionCard";
import Button from "../../../../components/Button/Button.jsx";
import { getAllEvents } from "../../../../services/eventServices";
import { FaCalendarAlt, FaSearch, FaSyncAlt, FaTimes } from "react-icons/fa";

const normalize = (value) =>
  (value || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const isValidDate = (value) => {
  if (!value) return false;
  const date = new Date(value);
  return !Number.isNaN(date.getTime());
};

const formatDateFr = (iso) => {
  if (!isValidDate(iso)) return "—";

  return new Date(iso).toLocaleDateString("fr-FR", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const DEFAULT_TYPE_EVENT = "Compétition";
const DEFAULT_ORGANISATEUR = "";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function EventSelectCard({
  value,
  onChange,
  initialType = DEFAULT_TYPE_EVENT,
  initialOrganisateur = DEFAULT_ORGANISATEUR,
}) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [q, setQ] = useState("");
  const [typeEvent, setTypeEvent] = useState(initialType || "");
  const [organisateur, setOrganisateur] = useState(initialOrganisateur || "");

  const fetchEvents = async () => {
    try {
      setError("");
      setLoading(true);

      const data = await getAllEvents();

      setEvents(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      setError("Impossible de charger la liste des événements.");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const allTypes = useMemo(() => {
    return Array.from(
      new Set(events.map((event) => event.type_event).filter(Boolean)),
    ).sort((a, b) => a.localeCompare(b, "fr"));
  }, [events]);

  const allOrganisateurs = useMemo(() => {
    return Array.from(
      new Set(events.map((event) => event.organisateur).filter(Boolean)),
    ).sort((a, b) => a.localeCompare(b, "fr"));
  }, [events]);

  const filtered = useMemo(() => {
    let result = [...events];

    if (typeEvent) {
      result = result.filter(
        (event) => normalize(event.type_event) === normalize(typeEvent),
      );
    }

    if (organisateur) {
      result = result.filter(
        (event) => normalize(event.organisateur) === normalize(organisateur),
      );
    }

    if (q.trim()) {
      const needle = normalize(q);

      result = result.filter((event) =>
        [
          event.titre,
          event.organisateur,
          event.type_event,
          event.level_event,
          event.cate_event,
          event.lieu_event,
          event.description,
        ]
          .map((item) => normalize(item))
          .join(" ")
          .includes(needle),
      );
    }

    result.sort((a, b) => {
      const timeA = isValidDate(a.start) ? new Date(a.start).getTime() : 0;
      const timeB = isValidDate(b.start) ? new Date(b.start).getTime() : 0;

      return timeA - timeB;
    });

    return result;
  }, [events, q, typeEvent, organisateur]);

  const selected = useMemo(() => {
    if (!value) return null;

    return events.find((event) => Number(event.id) === Number(value)) || null;
  }, [events, value]);

  const hasActiveFilters = q || typeEvent || organisateur;

  const handlePick = (eventId) => {
    const event = events.find((item) => Number(item.id) === Number(eventId));

    if (!event) return;

    onChange?.({
      event_id: event.id,
      titre: event.titre || "",
      start: event.start || null,
      lieu_event: event.lieu_event || "",
    });
  };

  const resetFilters = () => {
    setQ("");
    setTypeEvent("");
    setOrganisateur("");
  };

  return (
    <SectionCard
      id="fiche"
      title="Événement"
      subtitle="Sélectionnez l’événement concerné. Le nom, la date et le lieu seront repris automatiquement."
      icon={<FaCalendarAlt />}
      actions={
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            text={loading ? "Chargement..." : "Rafraîchir"}
            variant="outline"
            size="sm"
            onClick={fetchEvents}
            icon={FaSyncAlt}
          />

          <Button
            text="Tous les événements"
            variant="outline"
            size="sm"
            onClick={resetFilters}
            icon={FaTimes}
          />
        </div>
      }
    >
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950">
        <div className="text-sm font-bold text-slate-900 dark:text-white">
          Événement sélectionné
        </div>

        {selected ? (
          <div className="mt-2 text-sm text-slate-700 dark:text-slate-300">
            <div className="font-bold text-slate-900 dark:text-white">
              {selected.titre || "—"}
            </div>

            <div className="mt-1 text-slate-600 dark:text-slate-400">
              {formatDateFr(selected.start)} • {selected.lieu_event || "—"}
            </div>

            <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {selected.type_event || "—"}
              {selected.level_event ? ` • ${selected.level_event}` : ""}
            </div>
          </div>
        ) : (
          <div className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            Aucun événement sélectionné.
          </div>
        )}
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-12">
        <div className="md:col-span-6">
          <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm dark:border-slate-800 dark:bg-slate-950">
            <FaSearch className="text-slate-400" />

            <input
              value={q}
              onChange={(event) => setQ(event.target.value)}
              placeholder="Rechercher un événement..."
              className="w-full bg-transparent text-sm text-slate-800 outline-none placeholder:text-slate-400 dark:text-white"
            />
          </div>
        </div>

        <select
          value={typeEvent}
          onChange={(event) => setTypeEvent(event.target.value)}
          className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none shadow-sm dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200 md:col-span-3"
        >
          <option value="">Tous les types</option>

          {allTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>

        <select
          value={organisateur}
          onChange={(event) => setOrganisateur(event.target.value)}
          className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none shadow-sm dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200 md:col-span-3"
        >
          <option value="">Tous les organisateurs</option>

          {allOrganisateurs.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
        <span className="rounded-full bg-slate-100 px-3 py-1 font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          {events.length} événement{events.length > 1 ? "s" : ""} chargé
          {events.length > 1 ? "s" : ""}
        </span>

        <span className="rounded-full bg-blue-50 px-3 py-1 font-bold text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
          {filtered.length} affiché{filtered.length > 1 ? "s" : ""}
        </span>

        {hasActiveFilters ? (
          <button
            type="button"
            onClick={resetFilters}
            className="rounded-full bg-white px-3 py-1 font-bold text-slate-600 ring-1 ring-slate-200 transition hover:bg-slate-50 dark:bg-slate-950 dark:text-slate-300 dark:ring-slate-800 dark:hover:bg-slate-900"
          >
            Effacer les filtres
          </button>
        ) : null}
      </div>

      <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
        {error ? (
          <div className="border-b border-rose-200 bg-rose-50 p-4 text-sm font-bold text-rose-800 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="p-4 text-sm text-slate-600 dark:text-slate-300">
            Chargement des événements…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-5 text-sm text-slate-600 dark:text-slate-300">
            <p className="font-bold text-slate-900 dark:text-white">
              Aucun événement trouvé.
            </p>

            <p className="mt-1 text-slate-500 dark:text-slate-400">
              Vérifiez les filtres ou affichez tous les événements.
            </p>

            <button
              type="button"
              onClick={resetFilters}
              className="mt-4 inline-flex items-center justify-center rounded-2xl bg-[#013E8E] px-4 py-2.5 text-sm font-bold text-white transition hover:bg-[#012f6d]"
            >
              Afficher tous les événements
            </button>
          </div>
        ) : (
          <div className="max-h-[420px] overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-10 bg-slate-50 text-slate-600 shadow-sm dark:bg-slate-900 dark:text-slate-300">
                <tr className="text-left">
                  <th className="px-3 py-3 font-bold">Date</th>
                  <th className="px-3 py-3 font-bold">Titre</th>
                  <th className="px-3 py-3 font-bold">Type</th>
                  <th className="px-3 py-3 font-bold">Lieu</th>
                  <th className="px-3 py-3 text-right font-bold">Choisir</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {filtered.map((event) => {
                  const active = Number(event.id) === Number(value);

                  return (
                    <tr
                      key={event.id}
                      className={cn(
                        "transition",
                        active
                          ? "bg-emerald-50 dark:bg-emerald-500/10"
                          : "hover:bg-slate-50 dark:hover:bg-slate-900",
                      )}
                    >
                      <td className="whitespace-nowrap px-3 py-3 text-slate-700 dark:text-slate-300">
                        {formatDateFr(event.start)}
                      </td>

                      <td className="px-3 py-3">
                        <div className="font-bold text-slate-900 dark:text-white">
                          {event.titre || "—"}
                        </div>

                        <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                          {event.organisateur || "Organisateur non renseigné"}
                        </div>
                      </td>

                      <td className="px-3 py-3 text-slate-700 dark:text-slate-300">
                        {event.type_event || "—"}
                      </td>

                      <td className="px-3 py-3 text-slate-700 dark:text-slate-300">
                        {event.lieu_event || "—"}
                      </td>

                      <td className="px-3 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => handlePick(event.id)}
                          className={cn(
                            "inline-flex h-9 items-center justify-center rounded-xl px-3 text-sm font-bold transition",
                            active
                              ? "bg-emerald-600 text-white hover:bg-emerald-700"
                              : "bg-white text-slate-800 ring-1 ring-slate-200 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700 dark:hover:bg-slate-800",
                          )}
                        >
                          {active ? "Sélectionné" : "Choisir"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </SectionCard>
  );
}